import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ============================================================================
// Eduzz
// ============================================================================
export interface EduzzStats {
  hoje: { valor_cents: number; vendas: number };
  ontem: { valor_cents: number; vendas: number };
  ultimos_30d: { valor_cents: number; vendas: number };
  mrr_cents: number;
  reembolsos_30d: { valor_cents: number; count: number };
  top_produtos_30d: Array<{ produto_nome: string; valor_cents: number; vendas: number }>;
  produtos_nao_mapeados: Array<{ eduzz_product_id: string; count: number }>;
}

export function useEduzzStats(): UseQueryResult<EduzzStats> {
  return useQuery({
    queryKey: ['integrations', 'eduzz', 'stats'],
    queryFn: () => api<EduzzStats>('/api/integrations/eduzz/stats'),
    refetchInterval: 120_000,
  });
}

export interface ProductMap {
  eduzz_product_id: string;
  curso_central_id: number | null;
  produto_nome: string | null;
  curso_nome: string | null;
}

export function useProductMaps(): UseQueryResult<{ maps: ProductMap[] }> {
  return useQuery({
    queryKey: ['integrations', 'eduzz', 'maps'],
    queryFn: () => api<{ maps: ProductMap[] }>('/api/integrations/eduzz/product-map'),
  });
}

export function useUpsertProductMap(): ReturnType<
  typeof useMutation<
    { ok: true },
    Error,
    { eduzz_product_id: string; curso_central_id: number | null; produto_nome?: string }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<{ ok: true }>('/api/integrations/eduzz/product-map', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'eduzz'] }),
  });
}

// ============================================================================
// Meta Ads
// ============================================================================
export interface MetaAdsAccount {
  id: number;
  ad_account_id: string;
  nome: string;
  mentorada_id: number | null;
  ativa: number;
  ultimo_sync_em: string | null;
  ultimo_erro: string | null;
  criado_em: string;
}

export interface MetaAdsStats {
  hoje: { gasto_cents: number; leads: number; cliques: number };
  ontem: { gasto_cents: number; leads: number };
  ultimos_7d: { gasto_cents: number; leads: number };
  por_account: Array<{
    ad_account_id: string;
    nome: string;
    gasto_7d_cents: number;
    leads_7d: number;
    ultimo_sync_em: string | null;
  }>;
}

export function useMetaAdsAccounts(): UseQueryResult<{ accounts: MetaAdsAccount[] }> {
  return useQuery({
    queryKey: ['integrations', 'meta-ads', 'accounts'],
    queryFn: () => api<{ accounts: MetaAdsAccount[] }>('/api/integrations/meta-ads/accounts'),
  });
}

export function useMetaAdsStats(): UseQueryResult<MetaAdsStats> {
  return useQuery({
    queryKey: ['integrations', 'meta-ads', 'stats'],
    queryFn: () => api<MetaAdsStats>('/api/integrations/meta-ads/stats'),
    refetchInterval: 120_000,
  });
}

export function useCreateMetaAdsAccount(): ReturnType<
  typeof useMutation<
    MetaAdsAccount,
    Error,
    { ad_account_id: string; nome: string; token: string; mentorada_id?: number | null }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<MetaAdsAccount>('/api/integrations/meta-ads/accounts', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'meta-ads'] }),
  });
}

export function useSyncMetaAds(): ReturnType<
  typeof useMutation<{ accounts: number; days_synced: number; errors: number }, Error>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ accounts: number; days_synced: number; errors: number }>(
        '/api/integrations/meta-ads/sync',
        { method: 'POST' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'meta-ads'] }),
  });
}

export function useDeleteMetaAdsAccount(): ReturnType<typeof useMutation<void, Error, number>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/integrations/meta-ads/accounts/${id}`, { method: 'DELETE' }) as Promise<void>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'meta-ads'] }),
  });
}

// ============================================================================
// Marketing Command
// ============================================================================
export interface MCAgent {
  id: number;
  agent_key: string;
  nome: string;
  descricao: string | null;
  base_url: string;
  endpoint: string;
  metodo: string;
  auth_header: string | null;
  ativo: number;
  ultimo_check_em: string | null;
  ultimo_status: string | null;
  ultimo_http_code: number | null;
  ultimo_erro: string | null;
  latencia_ms: number | null;
  criado_em: string;
}

export interface MCStats {
  total: number;
  ok: number;
  erro: number;
  timeout: number;
  latencia_media_ms: number | null;
  agents: MCAgent[];
}

export function useMCStats(): UseQueryResult<MCStats> {
  return useQuery({
    queryKey: ['integrations', 'mc', 'stats'],
    queryFn: () => api<MCStats>('/api/integrations/mc/stats'),
    refetchInterval: 60_000,
  });
}

export function useUpsertMCAgent(): ReturnType<
  typeof useMutation<
    MCAgent,
    Error,
    {
      agent_key: string;
      nome: string;
      descricao?: string | null;
      base_url: string;
      endpoint: string;
      metodo?: 'GET' | 'POST' | 'PUT' | 'DELETE';
      auth_header?: string | null;
      auth_token?: string | null;
      ativo?: boolean;
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<MCAgent>('/api/integrations/mc/agents', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'mc'] }),
  });
}

export function usePingMCAgent(): ReturnType<
  typeof useMutation<
    { agent_id: number; status: string; http_code: number | null; latencia_ms: number; erro: string | null },
    Error,
    number
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/integrations/mc/agents/${id}/ping`, { method: 'POST' }) as Promise<{
        agent_id: number;
        status: string;
        http_code: number | null;
        latencia_ms: number;
        erro: string | null;
      }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'mc'] }),
  });
}

export function useDeleteMCAgent(): ReturnType<typeof useMutation<void, Error, number>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/integrations/mc/agents/${id}`, { method: 'DELETE' }) as Promise<void>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'mc'] }),
  });
}

// ============================================================================
// Evolution API
// ============================================================================
export interface EvolutionInstance {
  id: number;
  instance_name: string;
  base_url: string;
  numero: string | null;
  descricao: string | null;
  ativo: number;
  ultimo_status: string | null;
  mensagens_hoje: number;
  ultimo_sync_em: string | null;
  ultimo_erro: string | null;
}

export function useEvolutionInstances(): UseQueryResult<{ instances: EvolutionInstance[] }> {
  return useQuery({
    queryKey: ['integrations', 'evolution', 'instances'],
    queryFn: () => api<{ instances: EvolutionInstance[] }>('/api/integrations/evolution/instances'),
    refetchInterval: 60_000,
  });
}

export function useUpsertEvolutionInstance(): ReturnType<
  typeof useMutation<
    EvolutionInstance,
    Error,
    { instance_name: string; base_url: string; api_key: string; numero?: string | null; descricao?: string | null }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<EvolutionInstance>('/api/integrations/evolution/instances', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'evolution'] }),
  });
}

export function useSyncEvolution(): ReturnType<
  typeof useMutation<{ total: number; conectadas: number; erros: number }, Error>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ total: number; conectadas: number; erros: number }>('/api/integrations/evolution/sync', {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'evolution'] }),
  });
}

export function useDeleteEvolutionInstance(): ReturnType<typeof useMutation<void, Error, number>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/integrations/evolution/instances/${id}`, { method: 'DELETE' }) as Promise<void>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'evolution'] }),
  });
}

// ============================================================================
// Redes Sociais
// ============================================================================
export type SocialPlatform = 'youtube' | 'facebook' | 'instagram' | 'linkedin' | 'tiktok';

export interface SocialAccount {
  id: number;
  plataforma: SocialPlatform;
  nome: string;
  identificador: string;
  url_publica: string;
  api_habilitada: number;
  ativa: number;
  ultimo_sync_em: string | null;
  ultimo_erro: string | null;
}

export interface SocialStats {
  total: number;
  por_plataforma: Array<{
    plataforma: SocialPlatform;
    nome: string;
    url_publica: string;
    api_habilitada: number;
    seguidores: number | null;
    posts: number | null;
    ultimo_sync_em: string | null;
  }>;
}

export function useSocialStats(): UseQueryResult<SocialStats> {
  return useQuery({
    queryKey: ['integrations', 'social', 'stats'],
    queryFn: () => api<SocialStats>('/api/integrations/social/stats'),
    refetchInterval: 300_000,
  });
}

export function useSocialAccounts(): UseQueryResult<{ accounts: SocialAccount[] }> {
  return useQuery({
    queryKey: ['integrations', 'social', 'accounts'],
    queryFn: () => api<{ accounts: SocialAccount[] }>('/api/integrations/social/accounts'),
  });
}

export function useUpsertSocialAccount(): ReturnType<
  typeof useMutation<
    SocialAccount,
    Error,
    {
      plataforma: SocialPlatform;
      nome: string;
      identificador: string;
      url_publica: string;
      api_token?: string | null;
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<SocialAccount>('/api/integrations/social/accounts', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'social'] }),
  });
}

export function useSyncSocial(): ReturnType<
  typeof useMutation<{ total: number; ok: number; erros: number }, Error>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ total: number; ok: number; erros: number }>('/api/integrations/social/sync', {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'social'] }),
  });
}

export function useDeleteSocialAccount(): ReturnType<typeof useMutation<void, Error, number>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/integrations/social/accounts/${id}`, { method: 'DELETE' }) as Promise<void>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'social'] }),
  });
}

// ============================================================================
// Helpers
// ============================================================================
export function formatBRL(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}
